
import { useMemo, useState, useEffect } from 'react';
import MaterialReactTable from 'material-react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faSyncAlt, faTrashAlt, faUserTag } from '@fortawesome/free-solid-svg-icons';
import useUtils from '../../../utiles/useUtiles';
import Swal from 'sweetalert2';
import "./subscriber.scss";
import util from '../../../utiles/miniUtiles';
import NewSubscriberModal from './NewSubscriberModal';
import ModalResetOptions from './ModalResetOptions';

const SubscriberContent = () => {
    const REACT_REVREX_APP_URL = process.env.REACT_APP_REVREX_UI_URL;

    const { axiosRequest, baseUrl, toastInstance, loadingScreen } = useUtils()
    const axios = axiosRequest();
    const toast = toastInstance();

    const [usersList, setUsersList] = useState<any>({ count: 0, rows: [] });
    const [loading, setLoading] = useState<boolean>(false);
    const [newUser, setNewUser] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [searchText, setSearchText] = useState<string>('');
    const [pagination, setPagination] = useState<any>({
        pageIndex: 0,
        pageSize: 10
    });

    const [resetState, setResetState] = useState({
        account: {},
        showModal: false,
        account_id: 0
    });

    useEffect(() => {
        fetchUsers();
    }, [pagination.pageIndex, pagination.pageSize]);

    // Change password manually, it will NOT send any notification to the user.
    const handleChangePassword = async (account_id: number = 0) => {
        if (!account_id) account_id = 0;
        if (account_id === 0) return;
        Swal.fire({
            title: "Change Password",
            input: 'text',
            html: 'Please enter new password',
            showCancelButton: true,
            confirmButtonText: "Change",
            showLoaderOnConfirm: true,
            inputValidator: (password) => {
                if (!password) return "Password is required";
                if (password.length < 8) return "Password is too short";
                if (password.length > 20) return "Password is too long";
            }
        }).then((result) => {
            if (result.isConfirmed) {
                axios.put(`/account/update_password/${account_id}`, { password: result.value }).then((response) => {
                    if (response.data.error) {
                        Swal.fire("Error", response.data.msg, "error");
                    } else {
                        Swal.fire("Password changed successfully");
                    }
                });
            }
        });
    }


    // Send request to API to get the token for impersonate user
    const impersonateUserRequest = async (account_id: number = 0) => {
        try {
            const request = await axios.get(`/account/impersonate/${account_id}`);
            const { error, token } = request.data;
            if (error) throw new Error('Error to impersonate user');
            return {
                error: false,
                token
            }
        } catch (error) {
            return {
                error: true,
                msg: 'Error to impersonate user'
            }
        }
    }

    // Reset account, show option on which stage to reset
    const impersonateUser = (user: any) => {
        const account_id = user.id;
        // Confirm
        Swal.fire({
            title: `You are about to impersonate the account: ${user.name}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
        }).then(async (result) => {
            if (result.isConfirmed) {
                // Get user token to create new session in new tab
                const token = await impersonateUserRequest(account_id);
                if (token.error) {
                    Swal.fire('Error', token.msg, 'error');
                    return;
                }

                const url = REACT_REVREX_APP_URL + `/impersonate/?token=${token.token}`;
                if (process.env.REACT_APP_ENVIROMENT === "Local") {
                    Swal.fire("Token", url, "info");
                } else {
                    // Redirect to the new tab
                    window.open(url, '_blank');
                }
            }
        });

    }

    // Columns for the table
    const columns = useMemo(
        () => [
            {
                accessorKey: 'name',
                header: 'Name',
                minSize: 50,
                maxSize: 120,
                Cell: ({ cell }: any) => <span>{cell.getValue()}</span>,
            },
            {
                accessorKey: 'email',
                header: 'Email',
                minSize: 50,
                maxSize: 120,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
            },
            {
                accessorKey: 'actions1',
                header: 'Reset Account',
                minSize: 30,
                maxSize: 60,
                Cell: ({ cell }) => <div>
                    <FontAwesomeIcon
                        title='Reset Account'
                        icon={faSyncAlt}
                        className="isCursor ml-2"
                        onClick={() => {
                            setResetState({
                                ...resetState,
                                showModal: true,
                                account_id: cell.row.original.id,
                                account: cell.row.original
                            });
                        }}
                    />

                </div>,
            },
            {
                accessorKey: 'actions2',
                header: 'Delete Account',
                minSize: 30,
                maxSize: 60,
                Cell: ({ cell }) => <div>
                    <FontAwesomeIcon
                        title='Reset Account'
                        icon={faTrashAlt}
                        className="isCursor ml-2"
                        onClick={() => {
                            delete_user(cell.row.original)
                        }}
                    />
                </div>,
            },
            {
                accessorKey: 'actions3',
                header: 'Change Password Manually',
                minSize: 30,
                maxSize: 60,
                Cell: ({ cell }) => <div>
                    <FontAwesomeIcon
                        title='Change password'
                        icon={faLock}
                        className="isCursor ml-2"
                        onClick={() => {
                            const id = parseInt(cell.row.original.id);
                            handleChangePassword(id);
                        }}
                    />
                </div>,
            },
            {
                accessorKey: 'actions4',
                header: 'Impersonate user',
                minSize: 30,
                maxSize: 60,
                Cell: ({ cell }) => {
                    const email = cell.row.original.email;
                    if (email === 'john@revrex.com' || email === 'mike@rasmussentaxgroup.com') {
                        return null;
                    }
                    return (
                        <div>
                            <FontAwesomeIcon
                                title='Impersonate user'
                                icon={faUserTag}
                                className="isCursor ml-2"
                                onClick={() => {
                                    impersonateUser(cell.row.original);
                                }}
                            />
                        </div>
                    );
                },
            }
        ],
        [],
    );

    // Fetch users from the server
    const fetchUsers = async () => {
        setLoading(true);
        var queryString = `page=${pagination.pageIndex}&pageSize=${pagination.pageSize}`;
        if (searchText) {
            queryString += `&email=${searchText}`;
        }

        const request = await axios.get(`${baseUrl()}/accounts/list?${queryString}`);
        const { error, total, data } = request.data;
        setLoading(false);
        if (error) {
            toast.error('Error to fetch subscribers');
        } else {
            setUsersList({
                count: total,
                rows: data
            });
        }
    };

    // Handle key down event on search input
    const handleKeyDown = (e: any) => {
        if (e.key === 'Enter') {
            fetchUsers();
        }
    }

    // Reset account, send the request to the server
    const reset_account = async (option: string, account_id: number = 0) => {
        try {
            setLoading(true);
            const request = await axios.get(`/account/reset_user/${account_id}/${option}`);
            const { error, msg, errors, ibvData } = request.data;
            if (error) throw new Error(msg);
            setLoading(false);
            let html = `<b  class="text-left">REVREX account reset successfully</b>`;
            if (ibvData !== "") {
                html += `<div class="text-left"><b>IBV Data:</b> ${ibvData}</div>`;
            }
            if (errors && errors.length) {
                html += `<div class="text-left">The account was successfully reset in the REVREX database, but some errors occurred.</div>`;
                html += `<div class="text-center">-----------------------------------------</div>`;
                html += `<div class="text-left"><b>Errors:</b>`;
                errors.forEach((errors: {
                    action: string
                    error: string
                }) => {
                    html += `<div class="text-left">${errors.action} - <code>${errors.error}</code></div>`;
                });
                html += '</div>';
            }
            Swal.fire({
                title: 'User reset successfully',
                html: html,
                icon: 'success',
                confirmButtonText: 'Ok'
            }).then(() => {
                fetchUsers();
            }).finally(() => {
                setResetState({ ...resetState, showModal: false, account_id: 0 });
            });
        } catch (error: any) {
            setLoading(false);
            util.handleError(error);
        }
    };

    // Remove the user from the database and gl-database
    const delete_user = async (user: any) => {
        Swal.fire({
            title: 'Are you sure?',
            html: `<p>You are about to delete the account: <u><b>${user?.name}</b></u>?.</p> <p>This action will permanently remove ALL DATA associated with this user. This cannot be undone. Are you sure you want to proceed?</p>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    setLoading(true);
                    const request = await axios.get(`/account/delete_user/${user.id}`);
                    const { error } = request.data;
                    if (error) throw new Error('Error to delete user account');
                    setLoading(false);
                    toast.success('User deleted successfully');
                    fetchUsers();
                } catch (error) {
                    setLoading(false);
                    toast.error('Error to delete user account');
                }
            }
        });
    };

    // Create new subscriber without subscription


    return (
        <>
            <div className='mb-4 row'>
                <div className="col-md-6">
                    <div className="d-flex justify-start align-items-center">
                        <input
                            type={"text"}
                            name="searchText"
                            placeholder="Email"
                            className="form-control w-50"
                            value={searchText}
                            onKeyDown={handleKeyDown}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                        <button type="button" className="btn btn-md btn-custom-secondary ml-2" onClick={fetchUsers}>Search</button>
                    </div>
                </div>
                <div className="col-md-6">
                    <button onClick={() => setNewUser(true)} type="button" className="btn btn-md btn-primary float-right">Add Subscriber</button>
                </div>
            </div>

            <MaterialReactTable
                columns={columns}
                data={usersList?.rows}
                rowCount={usersList?.count}
                enableColumnOrdering={false}
                enableRowSelection={false}
                enableSelectAll={false}
                enableColumnActions={false}
                manualPagination
                onPaginationChange={setPagination}
                state={{ pagination }}
                enableSorting={false}
                enableGlobalFilter={false}
                enableFullScreenToggle={false}
                enableDensityToggle={false}
                enableHiding={false}
                enableColumnFilters
                enableFilters={false}
            />
            <NewSubscriberModal show={newUser} handleClose={() => setNewUser(false)} />
            {loadingScreen(loading)}

            {
                resetState.showModal &&
                <ModalResetOptions
                    reset_account={reset_account}
                    account_id={resetState.account_id}
                    showModal={resetState.showModal}
                    account={resetState.account}
                    handleClose={() => setResetState({ ...resetState, showModal: false })}
                />
            }
        </>
    );
}

export default SubscriberContent;